import {FC, useEffect, useState} from 'react'
import {Link, Outlet, Route, Routes, useNavigate} from 'react-router-dom'
import {CognitoUser} from 'amazon-cognito-identity-js'
import clsx from 'clsx'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {Auth} from 'aws-amplify'
import {toAbsoluteUrl} from '../../shared/utils'
import {toastDefaultConfig} from '../../shared/config/toastConfig'
import {getLogger} from '../../shared/utils/logUtils'
import {useCognitoAuth} from '../modules/auth'
import {
  cognitologin,
  cognitoSignUp,
  confirmCognitoMfa,
  finalizeCognitoLogin,
  requestPassword,
  selectCognitoTotpMfa,
} from '../modules/auth/core/_requests'
import {signInGoogle} from '../modules/auth/components/Login'
import {ofuscateEmail} from '../modules/auth/components/VerificationCodeInput'
import VerificationCodeInput from '../modules/auth/components/VerificationCodeInput'

// ---------------------------------------------------------------------------
// Shared layout — background, logo, card wrapper, footer
// ---------------------------------------------------------------------------

const SignInLayout: FC = () => {
  const {currentUser} = useCognitoAuth()
  const navigate = useNavigate()

  useEffect(() => {
    document.body.classList.add('signInPage')
    return () => {
      document.body.classList.remove('signInPage')
    }
  }, [])

  useEffect(() => {
    if (currentUser && currentUser.identity_id) {
      navigate('/dashboard', {replace: true})
    }
  }, [currentUser, navigate])

  if (currentUser && currentUser.identity_id) {
    return null
  }

  return (
    <div className='signin-page'>
      <div className='signin-bg-pattern'>
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-tl'
        />
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-br'
        />
      </div>

      <div className='signin-content'>
        <div className='signin-logo'>
          <img
            alt='OmicsBox Web'
            src={toAbsoluteUrl('/media/logos/ob_web_logo.svg')}
            className='signin-logo-img'
          />
        </div>

        <div className='signin-card'>
          <Outlet />
        </div>
      </div>

      <footer className='signin-footer'>
        <div className='signin-footer-content'>
          <span>© 2025 BioBam® — OmicsBox®</span>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/about/' target='_blank' rel='noreferrer'>
            About
          </a>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/contact-us/' target='_blank' rel='noreferrer'>
            Contact
          </a>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/blog/' target='_blank' rel='noreferrer'>
            Blog
          </a>
        </div>
      </footer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared — Google sign-in button (reused across login & registration)
// ---------------------------------------------------------------------------

const GoogleButton: FC = () => (
  <button type='button' onClick={signInGoogle} className='signin-btn signin-btn-google'>
    <svg className='signin-google-icon' viewBox='0 0 256 262' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='xMidYMid'>
      <path d='M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027' fill='#4285F4' />
      <path d='M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1' fill='#34A853' />
      <path d='M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782' fill='#FBBC05' />
      <path d='M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251' fill='#EA4335' />
    </svg>
    Continue with Google
  </button>
)

const Separator: FC = () => (
  <div className='signin-separator'><span>OR</span></div>
)

// ---------------------------------------------------------------------------
// Shared — validation feedback row
// ---------------------------------------------------------------------------

const ValidationRow: FC<{touched: boolean | undefined; error: string | undefined; label: string}> = ({
  touched,
  error,
  label,
}) => {
  if (!touched) return null
  return error ? (
    <div className='signin-validation signin-validation-error'>
      <span>✖</span> {label}
    </div>
  ) : (
    <div className='signin-validation signin-validation-ok'>
      <span>✓</span> {label}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const SignInLogin: FC = () => {
  const [loading, setLoading] = useState(false)
  const [loginStep, setLoginStep] = useState<'credentials' | 'sms_mfa' | 'totp_mfa'>('credentials')
  const [mfaUser, setMfaUser] = useState<CognitoUser | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const {saveCognitoAuth, setCurrentUser} = useCognitoAuth()
  const mfaInputId = loginStep === 'totp_mfa' ? 'signInTotpMfaCode' : 'signInSmsMfaCode'

  const formik = useFormik({
    initialValues: {email: '', password: ''},
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const user: CognitoUser = await cognitologin(values.email, values.password)
        const challenge =
          user.challengeName === 'SELECT_MFA_TYPE'
            ? await selectCognitoTotpMfa(user)
            : user.challengeName
        if (challenge === 'SMS_MFA' || challenge === 'SOFTWARE_TOKEN_MFA') {
          setMfaUser(user)
          setLoginStep(challenge === 'SMS_MFA' ? 'sms_mfa' : 'totp_mfa')
          setSubmitting(false)
          setLoading(false)
          return
        }
        if (challenge === 'MFA_SETUP' || challenge === 'NEW_PASSWORD_REQUIRED') {
          toast(
            challenge === 'NEW_PASSWORD_REQUIRED'
              ? 'You must set a new password before signing in. Use the forgot-password flow or contact support.'
              : 'Complete multi-factor setup in your account settings, then try again.',
            {...toastDefaultConfig, type: 'error'}
          )
          saveCognitoAuth(undefined)
          setCurrentUser(undefined)
          setSubmitting(false)
          setLoading(false)
          return
        }
        if (user) {
          await finalizeCognitoLogin(user, setCurrentUser, saveCognitoAuth)
        }
      } catch (error) {
        saveCognitoAuth(undefined)
        setCurrentUser(undefined)
        setStatus('Invalid username or password.')
        setSubmitting(false)
        setLoading(false)
        // @ts-ignore
        toast(error.message, {...toastDefaultConfig, type: 'error'})
        getLogger().error(error)
      }
      setSubmitting(false)
      setLoading(false)
    },
  })

  const submitMfa = async () => {
    if (!mfaUser || !mfaCode.trim()) return
    setLoading(true)
    try {
      const mfaType = loginStep === 'totp_mfa' ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA'
      const user = await confirmCognitoMfa(mfaUser, mfaCode.trim(), mfaType)
      await finalizeCognitoLogin(user, setCurrentUser, saveCognitoAuth)
      setLoginStep('credentials')
      setMfaUser(null)
      setMfaCode('')
    } catch (error) {
      // @ts-ignore
      toast(error.message || 'Invalid verification code', {...toastDefaultConfig, type: 'error'})
      getLogger().error(error)
    } finally {
      setLoading(false)
    }
  }

  const cancelMfa = () => {
    setLoginStep('credentials')
    setMfaUser(null)
    setMfaCode('')
    saveCognitoAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <>
      <h1 className='signin-title'>Sign in</h1>

      {formik.status && <div className='signin-alert'>{formik.status}</div>}

      {loginStep !== 'credentials' ? (
        <div className='signin-form'>
          <p className='signin-subtitle'>Two-step verification</p>
          <p className='signin-muted'>
            {loginStep === 'totp_mfa'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Enter the verification code sent to your phone via SMS.'}
          </p>
          <div className='signin-field'>
            <label htmlFor={mfaInputId} className='signin-label'>
              {loginStep === 'totp_mfa' ? 'Authenticator code' : 'SMS code'}
            </label>
            <input
              id={mfaInputId}
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              className='signin-input'
              placeholder='6-digit code'
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void submitMfa() }
              }}
            />
          </div>
          <button
            type='button'
            className='signin-btn signin-btn-primary'
            disabled={loading || mfaCode.length < 6}
            onClick={() => void submitMfa()}
          >
            Verify and sign in
          </button>
          <button
            type='button'
            className='signin-btn signin-btn-secondary'
            disabled={loading}
            onClick={cancelMfa}
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <>
          <GoogleButton />
          <Separator />

          <form className='signin-form' onSubmit={formik.handleSubmit} noValidate>
            <div className='signin-field'>
              <label htmlFor='signInEmail' className='signin-label'>Email</label>
              <input
                id='signInEmail' type='email' autoComplete='off' placeholder='your@email.com'
                className={clsx('signin-input', {
                  'signin-input-error': formik.touched.email && formik.errors.email,
                })}
                {...formik.getFieldProps('email')}
              />
              {formik.touched.email && formik.errors.email && (
                <div className='signin-field-error'>{formik.errors.email}</div>
              )}
            </div>
            <div className='signin-field'>
              <label htmlFor='signInPassword' className='signin-label'>Password</label>
              <input
                id='signInPassword' type='password' placeholder='Password'
                className={clsx('signin-input', {
                  'signin-input-error': formik.touched.password && formik.errors.password,
                })}
                {...formik.getFieldProps('password')}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='signin-field-error'>{formik.errors.password}</div>
              )}
            </div>
            <button
              type='submit'
              className='signin-btn signin-btn-primary'
              disabled={loading || formik.isSubmitting || !formik.isValid}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <Link to='forgot-password' className='signin-link'>Forgot your password?</Link>
          </form>

          <div className='signin-signup'>
            Don&apos;t have an account?{' '}
            <Link to='registration' className='signin-signup-link'>Sign up</Link>
          </div>
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

const registrationSchema = Yup.object().shape({
  firstname: Yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('First name is required'),
  email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required'),
  lastname: Yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Last name is required'),
  password: Yup.string()
    .min(3, 'Must be at least 8 characters')
    .max(50, 'Maximum 50 symbols')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
      'Password must contain uppercase, lowercase, number and special character'
    )
    .required('Password is required'),
})

const SignInRegistration: FC = () => {
  const [loading, setLoading] = useState(false)
  const {saveCognitoAuth, setCurrentUser, setConfirmSignUp} = useCognitoAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {firstname: '', lastname: '', email: '', password: ''},
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const userRegistered: CognitoUser = await cognitoSignUp(
          values.email, values.firstname, values.lastname, values.password
        )
        saveCognitoAuth(userRegistered)
        setCurrentUser({
          email: values.email,
          name: `${values.firstname} ${values.lastname}`,
          sub: '', identity_id: '', email_verified: false, updated_at: '',
          notifEmailJobStatus: false,
        })
        setConfirmSignUp(true)
        navigate('../validate-registration')
      } catch (error) {
        saveCognitoAuth(undefined)
        setStatus('The registration details are incorrect')
        setSubmitting(false)
        setLoading(false)
        // @ts-ignore
        toast(error.message, {...toastDefaultConfig, type: 'error'})
        getLogger().error(error)
      }
    },
  })

  return (
    <>
      <h1 className='signin-title'>Create account</h1>

      <GoogleButton />
      <Separator />

      <form className='signin-form' onSubmit={formik.handleSubmit} noValidate>
        <div className='signin-field'>
          <label htmlFor='regEmail' className='signin-label'>Email</label>
          <input
            id='regEmail' type='email' autoComplete='off' placeholder='your@email.com'
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.email && formik.errors.email,
            })}
            {...formik.getFieldProps('email')}
          />
        </div>

        <div className='signin-field-row'>
          <div className='signin-field'>
            <label htmlFor='regFirstname' className='signin-label'>Given name</label>
            <input
              id='regFirstname' type='text' autoComplete='off' placeholder='Given name'
              className={clsx('signin-input', {
                'signin-input-error': formik.touched.firstname && formik.errors.firstname,
              })}
              {...formik.getFieldProps('firstname')}
            />
          </div>
          <div className='signin-field'>
            <label htmlFor='regLastname' className='signin-label'>Family name</label>
            <input
              id='regLastname' type='text' autoComplete='off' placeholder='Family name'
              className={clsx('signin-input', {
                'signin-input-error': formik.touched.lastname && formik.errors.lastname,
              })}
              {...formik.getFieldProps('lastname')}
            />
          </div>
        </div>

        <div className='signin-field'>
          <label htmlFor='regPassword' className='signin-label'>Password</label>
          <input
            id='regPassword' type='password' autoComplete='off' placeholder='Password'
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.password && formik.errors.password,
            })}
            {...formik.getFieldProps('password')}
          />
        </div>

        <div className='signin-validations'>
          <ValidationRow touched={formik.touched.email} error={formik.errors.email} label='Valid email' />
          <ValidationRow touched={formik.touched.firstname} error={formik.errors.firstname} label='Valid given name' />
          <ValidationRow touched={formik.touched.lastname} error={formik.errors.lastname} label='Valid family name' />
          <ValidationRow
            touched={formik.touched.password}
            error={formik.errors.password}
            label='Uppercase, lowercase, number, special character, 8+ chars'
          />
        </div>

        <button
          type='submit'
          className='signin-btn signin-btn-primary'
          disabled={loading || formik.isSubmitting || !formik.isValid}
        >
          {loading ? 'Creating account...' : 'Sign up'}
        </button>
      </form>

      <div className='signin-signup'>
        Already have an account?{' '}
        <Link to='..' className='signin-signup-link'>Sign in</Link>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Forgot Password
// ---------------------------------------------------------------------------

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required'),
})

const SignInForgotPassword: FC = () => {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  // eslint-disable-next-line prefer-const
  let {currentUser, setCurrentUser} = useCognitoAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        requestPassword(values.email)
          .then(() => {
            getLogger().log('Password reset email sent')
            if (currentUser) {
              currentUser.email = values.email
            } else {
              currentUser = {
                email: values.email, name: '', sub: '', identity_id: '',
                email_verified: false, updated_at: '',
              }
            }
            setCurrentUser(currentUser)
            setHasErrors(false)
            setLoading(false)
            navigate('../forgot-password-validation')
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus('The login detail is incorrect')
          })
      }, 1000)
    },
  })

  return (
    <>
      <h1 className='signin-title'>Forgot your password?</h1>
      <p className='signin-muted' style={{textAlign: 'center'}}>
        Enter your email below and we will send a message to reset your password.
      </p>

      {hasErrors === true && (
        <div className='signin-alert'>
          Sorry, looks like there are some errors detected, please try again.
        </div>
      )}

      {hasErrors === false && (
        <div className='signin-success'>
          Sent password reset. Please check your email.
        </div>
      )}

      <form className='signin-form' onSubmit={formik.handleSubmit} noValidate>
        <div className='signin-field'>
          <label htmlFor='fpEmail' className='signin-label'>Email</label>
          <input
            id='fpEmail' type='email' autoComplete='off' placeholder='your@email.com'
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.email && formik.errors.email,
            })}
            {...formik.getFieldProps('email')}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='signin-field-error'>{formik.errors.email}</div>
          )}
        </div>

        <button
          type='submit'
          className='signin-btn signin-btn-primary'
          disabled={loading || formik.isSubmitting || !formik.isValid}
        >
          {loading ? 'Sending...' : 'Reset my password'}
        </button>
      </form>

      <div className='signin-signup'>
        <Link to='..' className='signin-signup-link'>Back to sign in</Link>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Validate Registration (check your email)
// ---------------------------------------------------------------------------

const SignInValidateRegistration: FC = () => {
  const {currentUser} = useCognitoAuth()
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  const handleResend = async () => {
    if (!currentUser?.email) return
    setResending(true)
    try {
      await Auth.resendSignUp(currentUser.email)
      setResent(true)
      toast('Activation link resent. Please check your email.', {
        ...toastDefaultConfig, type: 'success',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend activation link'
      toast(message, {...toastDefaultConfig, type: 'error'})
      getLogger().error(error)
    } finally {
      setResending(false)
    }
  }

  return (
    <>
      <h1 className='signin-title'>Check your email</h1>

      <div className='signin-muted' style={{textAlign: 'center'}}>
        <p>
          We have sent an activation link to{' '}
          <strong>{currentUser?.email ? ofuscateEmail(currentUser.email) : 'your email'}</strong>.
        </p>
        <p>Click the link in the email to activate your account, then sign in below.</p>
      </div>

      <Link to='..' className='signin-btn signin-btn-primary' style={{textDecoration: 'none'}}>
        Go to Sign In
      </Link>

      <div className='signin-signup' style={{borderTop: 'none', paddingTop: '12px'}}>
        <span className='signin-muted' style={{margin: 0}}>Didn&apos;t receive the email?&nbsp;</span>
        <button
          type='button'
          className='signin-inline-btn'
          onClick={handleResend}
          disabled={resending || resent}
        >
          {resending ? 'Sending...' : resent ? 'Link resent' : 'Resend activation link'}
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Validate Forgot Password (enter code + new password)
// ---------------------------------------------------------------------------

const resetSchema = Yup.object().shape({
  code: Yup.string().length(6, 'Must be composed of 6 numbers').required('Verification code is required').matches(/[0-9]{6}/, 'Must be composed of 6 numbers'),
  password: Yup.string()
    .min(3, 'Must be at least 8 characters').max(50, 'Maximum 50 symbols')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
      'Password must contain uppercase, lowercase, number and special character'
    )
    .required('Password is required'),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    }),
})

const SignInValidateForgotPassword: FC = () => {
  const [loading, setLoading] = useState(false)
  const [verificationCodeComplete, setVerificationCodeComplete] = useState(false)
  const {verificationCode, currentUser} = useCognitoAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {code: '', password: '', changepassword: ''},
    validationSchema: resetSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        if (!verificationCode) throw new Error('No verification code')
        if (!currentUser) throw new Error('User information missing!')
        if (!currentUser.email) throw new Error('email missing!')
        const value = await Auth.forgotPasswordSubmit(currentUser.email, verificationCode, values.password)
        getLogger().log(value)
        navigate('..')
      } catch (e) {
        setStatus(e)
        setSubmitting(false)
        setLoading(false)
        // @ts-ignore
        toast(e.message, {...toastDefaultConfig, type: 'error'})
        getLogger().error(e)
      }
    },
  })

  const codeCompleted = () => {
    setVerificationCodeComplete(true)
  }

  return (
    <>
      <h1 className='signin-title'>Reset password</h1>

      <p className='signin-muted' style={{textAlign: 'center'}}>
        We have sent a password reset code by email to{' '}
        <strong>{currentUser?.email ? ofuscateEmail(currentUser.email) : 'your email'}</strong>.
        Enter it below to reset your password.
      </p>

      <form className='signin-form' onSubmit={formik.handleSubmit} noValidate>
        <div className='signin-field'>
          <label htmlFor='fpvCode' className='signin-label'>Verification code</label>
          <VerificationCodeInput
            {...formik.getFieldProps('code')}
            onComplete={codeCompleted}
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.code && formik.errors.code,
            })}
          />
          {formik.touched.code && formik.errors.code && (
            <div className='signin-field-error'>{formik.errors.code}</div>
          )}
        </div>

        <div className='signin-field'>
          <label htmlFor='fpvPassword' className='signin-label'>New password</label>
          <input
            id='fpvPassword' type='password' autoComplete='off' placeholder='New password'
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.password && formik.errors.password,
            })}
            {...formik.getFieldProps('password')}
          />
        </div>

        <div className='signin-field'>
          <label htmlFor='fpvConfirm' className='signin-label'>Confirm new password</label>
          <input
            id='fpvConfirm' type='password' autoComplete='off' placeholder='Confirm password'
            className={clsx('signin-input', {
              'signin-input-error': formik.touched.changepassword && formik.errors.changepassword,
            })}
            {...formik.getFieldProps('changepassword')}
          />
        </div>

        <div className='signin-validations'>
          <ValidationRow
            touched={formik.touched.password}
            error={formik.errors.password}
            label='Uppercase, lowercase, number, special character, 8+ chars'
          />
          <ValidationRow
            touched={formik.touched.changepassword}
            error={formik.errors.changepassword}
            label='Passwords must match'
          />
        </div>

        <button
          type='submit'
          className='signin-btn signin-btn-primary'
          disabled={!verificationCodeComplete || loading || formik.isSubmitting || !formik.isValid}
        >
          {loading ? 'Resetting...' : 'Change password'}
        </button>
      </form>

      <div className='signin-signup'>
        <Link to='..' className='signin-signup-link'>Back to sign in</Link>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Router — maps /signin/* sub-routes to the layout + components
// ---------------------------------------------------------------------------

const SignInPage: FC = () => (
  <Routes>
    <Route element={<SignInLayout />}>
      <Route index element={<SignInLogin />} />
      <Route path='registration' element={<SignInRegistration />} />
      <Route path='forgot-password' element={<SignInForgotPassword />} />
      <Route path='forgot-password-validation' element={<SignInValidateForgotPassword />} />
      <Route path='validate-registration' element={<SignInValidateRegistration />} />
    </Route>
  </Routes>
)

export {SignInPage}
